// Route Configuration
// ===================
// Two modules: ADMIN MODULE and USER MODULE (see sections below).

import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES, WEB_HISTORY_ONLY } from '../config/constants';
import PrivateRoute from '../components/PrivateRoute';
import AdminRoute from '../components/AdminRoute';
import { useAuth } from '../context/AuthContext';
import Loader from '../components/Loader';
import { isPortalAdmin } from '../utils/portalRoles';

// ─── User module (customer) screens ─────────────────────────────────────────
import QRLogin from '../pages/customer/QRLogin';
import MobileWelcome from '../pages/customer/MobileWelcome';
import MobileLogin from '../pages/customer/MobileLogin';
import OTPVerification from '../pages/customer/OTPVerification';
import ProfileSetup from '../pages/customer/ProfileSetup';
import AccountDeregister from '../pages/customer/AccountDeregister';
import TermsAndConditions from '../pages/TermsAndConditions';

// ─── Admin module screens ───────────────────────────────────────────────────
import AdminLogin from '../pages/admin/AdminLogin';
import AdminDashboard from '../pages/admin/AdminDashboard';
import BrandManagement from '../pages/admin/BrandManagement';
import SalonManagement from '../pages/admin/SalonManagement';
import MirrorManagement from '../pages/admin/MirrorManagement';
import CategoryManagement from '../pages/admin/CategoryManagement';
import ServiceManagement from '../pages/admin/ServiceManagement';
import StylistManagement from '../pages/admin/StylistManagement';
import TransactionList from '../pages/admin/TransactionList';
import TransactionDetails from '../pages/admin/TransactionDetails';
import AdminOngoingServices from '../pages/admin/OngoingServices';
import ConsultationReport from '../pages/admin/ConsultationReport';
import UserManagement from '../pages/admin/UserManagement';
import FeedbackList from '../pages/admin/FeedbackList';
import FeedManagement from '../pages/admin/FeedManagement';
import PromotionalFeedManagement from '../pages/admin/PromotionalFeedManagement';
import EngagementServiceManagement from '../pages/admin/EngagementServiceManagement';

const WelcomeScreen = React.lazy(() => import('../pages/customer/WelcomeScreen'));
const CustomerDashboard = React.lazy(() => import('../pages/customer/CustomerDashboard'));
const ServiceSelection = React.lazy(() => import('../pages/customer/ServiceSelection'));
const ServiceConfirmation = React.lazy(() => import('../pages/customer/ServiceConfirmation'));
const EnjoyService = React.lazy(() => import('../pages/customer/EnjoyService'));
const CustomerOngoingServices = React.lazy(() => import('../pages/customer/OngoingServices'));
const TransactionHistory = React.lazy(() => import('../pages/customer/TransactionHistory'));
const TransactionHistoryDetail = React.lazy(() => import('../pages/customer/TransactionHistoryDetail'));
const FeedbackScreen = React.lazy(() => import('../pages/customer/FeedbackScreen'));
const PaymentScreen = React.lazy(() => import('../pages/customer/PaymentScreen'));
const PaymentConfirmation = React.lazy(() => import('../pages/customer/PaymentConfirmation'));
const PhotoCapture = React.lazy(() => import('../pages/customer/PhotoCapture'));
const KioskApp = React.lazy(() => import('../kiosk/KioskApp'));

// For testing: "/" opens admin module. Logged-in admins go to dashboard; else admin login.
const DefaultRedirect = () => {
  const { token, user } = useAuth();
  const storedUserStr = localStorage.getItem('user');
  const storedUser = storedUserStr ? (() => { try { return JSON.parse(storedUserStr); } catch { return null; } })() : null;
  const finalUser = user || storedUser;

  if (token && finalUser && isPortalAdmin(finalUser.role)) {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }
  return <Navigate to={ROUTES.ADMIN_LOGIN} replace />;
};

const CustomerHistoryRedirect = () => (
  <PrivateRoute>
    <Navigate to={ROUTES.TRANSACTION_HISTORY} replace />
  </PrivateRoute>
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loader variant="light" fullScreen message="Loading…" />}>
    <Routes>
      {/* ═══════════════════════════════════════════════════════════════════
          PUBLIC (shared entry points)
      ═══════════════════════════════════════════════════════════════════ */}
      <Route path={ROUTES.QR_LOGIN} element={<QRLogin />} />
      <Route path={ROUTES.MOBILE_WELCOME} element={<MobileWelcome />} />
      <Route path={ROUTES.MOBILE_LOGIN} element={<MobileLogin />} />
      <Route path={ROUTES.OTP_VERIFICATION} element={<OTPVerification />} />
      <Route path={ROUTES.PROFILE_SETUP} element={<ProfileSetup />} />
      <Route path={ROUTES.ACCOUNT_DEREGISTER} element={<AccountDeregister />} />
      <Route path={ROUTES.TERMS_AND_CONDITIONS} element={<TermsAndConditions />} />
      <Route path={ROUTES.ADMIN_LOGIN} element={<AdminLogin />} />
      <Route path={`${ROUTES.KIOSK}/*`} element={<KioskApp />} />

      {/* ═══════════════════════════════════════════════════════════════════
          USER MODULE (customer flow – requires customer auth)
          Screens: QR Login → Mobile Welcome → Mobile Login → OTP → Profile Setup
          → Welcome → Dashboard → Service Select/Confirm/Enjoy/Ongoing
          → Transaction History → Feedback → Payment → Photo Capture
      ═══════════════════════════════════════════════════════════════════ */}
      <Route path={ROUTES.WELCOME} element={WEB_HISTORY_ONLY ? <CustomerHistoryRedirect /> : <PrivateRoute><WelcomeScreen /></PrivateRoute>} />
      <Route path={ROUTES.DASHBOARD} element={WEB_HISTORY_ONLY ? <CustomerHistoryRedirect /> : <PrivateRoute><CustomerDashboard /></PrivateRoute>} />
      <Route path={ROUTES.SERVICE_SELECT} element={WEB_HISTORY_ONLY ? <CustomerHistoryRedirect /> : <PrivateRoute><ServiceSelection /></PrivateRoute>} />
      <Route path={ROUTES.SERVICE_CONFIRM} element={WEB_HISTORY_ONLY ? <CustomerHistoryRedirect /> : <PrivateRoute><ServiceConfirmation /></PrivateRoute>} />
      <Route path={ROUTES.SERVICE_ENJOY} element={WEB_HISTORY_ONLY ? <CustomerHistoryRedirect /> : <PrivateRoute><EnjoyService /></PrivateRoute>} />
      <Route path={ROUTES.SERVICE_ONGOING} element={WEB_HISTORY_ONLY ? <CustomerHistoryRedirect /> : <PrivateRoute><CustomerOngoingServices /></PrivateRoute>} />
      <Route path={ROUTES.TRANSACTION_HISTORY} element={<PrivateRoute><TransactionHistory /></PrivateRoute>} />
      <Route path={ROUTES.TRANSACTION_HISTORY_DETAIL} element={<PrivateRoute><TransactionHistoryDetail /></PrivateRoute>} />
      <Route path={ROUTES.FEEDBACK} element={WEB_HISTORY_ONLY ? <CustomerHistoryRedirect /> : <PrivateRoute><FeedbackScreen /></PrivateRoute>} />
      <Route path={ROUTES.PAYMENT} element={WEB_HISTORY_ONLY ? <CustomerHistoryRedirect /> : <PrivateRoute><PaymentScreen /></PrivateRoute>} />
      <Route path={ROUTES.PAYMENT_CONFIRMATION} element={WEB_HISTORY_ONLY ? <CustomerHistoryRedirect /> : <PrivateRoute><PaymentConfirmation /></PrivateRoute>} />
      <Route path={ROUTES.PHOTO_CAPTURE} element={WEB_HISTORY_ONLY ? <CustomerHistoryRedirect /> : <PrivateRoute><PhotoCapture /></PrivateRoute>} />

      {/* ═══════════════════════════════════════════════════════════════════
          ADMIN MODULE (admin flow – requires admin/superadmin auth)
          Screens: Admin Login → Dashboard → Brands, Salons, Mirrors, Categories,
          Services, Stylists, Transactions, Reports, Users, Feedback, Feeds,
          Engagement Services, Approvals
      ═══════════════════════════════════════════════════════════════════ */}
      <Route path={ROUTES.ADMIN_DASHBOARD} element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_BRANDS} element={<AdminRoute roles={['superadmin', 'brandadmin']}><BrandManagement /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_SALONS} element={<AdminRoute><SalonManagement /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_MIRRORS} element={<AdminRoute><MirrorManagement /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_CATEGORIES} element={<AdminRoute><CategoryManagement /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_SERVICES} element={<AdminRoute><ServiceManagement /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_STYLISTS} element={<AdminRoute><StylistManagement /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_FEATURE_PACKS} element={<AdminRoute><Navigate to={ROUTES.ADMIN_DASHBOARD} replace /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_TRANSACTIONS} element={<AdminRoute><TransactionList /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_TRANSACTION_DETAILS} element={<AdminRoute><TransactionDetails /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_ONGOING_SERVICES} element={<AdminRoute><AdminOngoingServices /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_REPORTS} element={<AdminRoute><ConsultationReport /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_USERS} element={<AdminRoute><UserManagement /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_FEEDBACK} element={<AdminRoute><FeedbackList /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_FEEDS} element={<AdminRoute><FeedManagement /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_PROMOTIONAL_FEEDS} element={<AdminRoute><PromotionalFeedManagement /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_ENGAGEMENT_SERVICES} element={<AdminRoute><EngagementServiceManagement /></AdminRoute>} />
      <Route path={ROUTES.ADMIN_APPROVALS} element={<AdminRoute><Navigate to={ROUTES.ADMIN_DASHBOARD} replace /></AdminRoute>} />

      {/* Default: for testing open admin module (admin login or dashboard if already logged in) */}
      <Route path="/" element={<DefaultRedirect />} />
    </Routes>
    </Suspense>
  );
};

export default AppRoutes;
